import { AddressError } from '@components/Address/AddressError';
import { TireHotelPickupAddressSuggestion } from '@components/BookingFlow/Common/TireHotelPickupAddressSuggestion';
import { t } from '@lingui/core/macro';
import { SalesItemWheelsPickupOptionRecord, TireHotelLocationRecord } from '@navio/nsdk';
import { BOOKING_AVAILABLE_COUNTRY_CODES, SALES_ITEM_BOOKING_FLOW_TYPE, SelectedSalesItemsCar } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiInfoBanner } from 'noddi-ui-web';
import { compareLicensePlates, formatCurrencyAmount } from 'noddi-util';
import {
  AddressRecoveryForm,
  AddressSearch,
  getHardCodedPickupText,
  getLongAddressName,
  getPickupAddressAwaitingTireHotelDecision,
  TireHotelLocationsSelect,
  useAddressResolution,
  useBookingActions,
  useBookingAddress,
  useBookingSelectedSalesItemsCars
} from 'noddi-web';
import { useState } from 'react';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../../utils/commonTranslations';
import { MultipleCarSelectionBlock } from './MultipleCarSelectionBlock';

type WheelStorageOptionProps = {
  tireHotelSalesItem: SalesItemWheelsPickupOptionRecord;
  carsWithTirePickupOptions: SelectedSalesItemsCar[];
};

export const TireHotelMultipleCars = ({ tireHotelSalesItem, carsWithTirePickupOptions }: WheelStorageOptionProps) => {
  const { currentBrand } = useCurrentBrand();
  const {
    name,
    price,
    salesItemId,
    requiresPickupAddressSelection,
    mutuallyExclusiveSalesItemIds,
    isAddonsAvailable,
    isWheelPickupRequired
  } = tireHotelSalesItem;

  const selectedAddress = useBookingAddress();
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();

  const selectedSalesItemsCarsCurrentItem = selectedSalesItemsCars.filter((item) =>
    carsWithTirePickupOptions.some(
      (car) => compareLicensePlates(item.licensePlate, car.licensePlate) && item.salesItemId === salesItemId
    )
  );

  const hasAnySelectedSalesItems = !!selectedSalesItemsCarsCurrentItem.length;

  const [isTirePickUpAtHomeExpanded, setIsTirePickUpAtHomeExpanded] = useState(hasAnySelectedSalesItems);

  // Check if any car has tire hotel location or custom address
  const customPickUpAddress = selectedSalesItemsCars.find((item) => item.metaData?.selectedTireHotelPickUpAddress);
  const selectedTireHotelLocation = selectedSalesItemsCars.find((item) => item.metaData?.selectedTireHotelLocation)
    ?.metaData?.selectedTireHotelLocation;

  // UI state for toggling between tire hotel locations and custom address
  const [useCustomAddress, setUseCustomAddress] = useState(!!customPickUpAddress && !selectedTireHotelLocation);

  const addressAwaitingDecision = getPickupAddressAwaitingTireHotelDecision(customPickUpAddress);

  const { removeSalesItems, addSalesItems } = useBookingActions();

  const params = carsWithTirePickupOptions.map(({ licensePlate, bookingCategoryType }) => ({
    licensePlate,
    salesItemId,
    type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
    mutuallyExclusiveSalesItemIds,
    isAddonsAvailable,
    isWheelPickupRequired,
    bookingCategoryType
  }));

  const handleTireHotelLocationSelection = (location: TireHotelLocationRecord | undefined) => {
    if (location) {
      setUseCustomAddress(false);
      const paramsWithLocation = carsWithTirePickupOptions.map(({ licensePlate, bookingCategoryType }) => ({
        licensePlate,
        salesItemId,
        type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
        mutuallyExclusiveSalesItemIds,
        metaData: {
          // todo ts-package error fix
          selectedTireHotelPickUpAddress: location.address,
          selectedTireHotelLocation: location
        },
        isAddonsAvailable,
        isWheelPickupRequired,
        bookingCategoryType
      }));
      addSalesItems(paramsWithLocation);
    } else {
      removeSalesItems(params);
    }
  };

  const pickUpAtHomeToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (hasAnySelectedSalesItems) {
      removeSalesItems(params);
      setIsTirePickUpAtHomeExpanded(false);
    } else {
      addSalesItems(params);
      setIsTirePickUpAtHomeExpanded(true);
    }
    e.stopPropagation();
  };

  const {
    recoveryFormProps,
    resolve: createAddressFromPlaceId,
    clear: resetCreateAddressFromPlaceId,
    isPending,
    error: addressError,
    data: createdAddress
  } = useAddressResolution({
    onSuccess: ({ isInDeliveryArea, ...rest }) => {
      if (isInDeliveryArea) {
        const params = carsWithTirePickupOptions.map(({ licensePlate, bookingCategoryType }) => ({
          licensePlate,
          salesItemId,
          type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
          mutuallyExclusiveSalesItemIds,
          metaData: { selectedTireHotelPickUpAddress: rest },
          isAddonsAvailable,
          isWheelPickupRequired,
          bookingCategoryType
        }));

        addSalesItems(params);
      } else {
        removeSalesItems(params);
      }
    },
    onError: () => {
      removeSalesItems(params);
    }
  });

  const isFree = price.priceGross.amount === 0;
  const priceFormatted = isFree
    ? t`Free`
    : formatCurrencyAmount({
        money: price.priceGross,
        decimals: 0
      });

  if (!requiresPickupAddressSelection) {
    return (
      <NoddiBasicCard
        onClick={() => setIsTirePickUpAtHomeExpanded(!isTirePickUpAtHomeExpanded)}
        className='flex flex-col gap-2'
      >
        <div className='flex flex-col gap-2'>
          <div className='flex items-baseline justify-between gap-2'>
            <span className='text-lg font-medium'>{name}</span>
            <div
              className={cn('transition-transform duration-300 ease-out', isTirePickUpAtHomeExpanded && 'rotate-180')}
            >
              <NoddiIcon name='ChevronDown' />
            </div>
          </div>
          <span>{getHardCodedPickupText(selectedSalesItemsCars, 'home')}</span>
          <div className='-ml-1 flex items-baseline gap-1'>
            <NoddiIcon name='MapPin' />
            {selectedAddress && <span>{getLongAddressName(selectedAddress)}</span>}
          </div>
          <div className='flex items-center justify-between'>
            <span className={cn('font-semibold', isFree && 'text-successDark')}>{priceFormatted}</span>
            <NoddiButton
              variant='outline'
              size='small'
              onClick={pickUpAtHomeToggle}
              startIcon={hasAnySelectedSalesItems ? 'Minus' : 'Plus'}
            >
              {hasAnySelectedSalesItems ? `${t`Remove`} (${selectedSalesItemsCarsCurrentItem.length})` : t`Add`}
            </NoddiButton>
          </div>
        </div>
        {isTirePickUpAtHomeExpanded && (
          <MultipleCarSelectionBlock
            carsWithTirePickupOptions={carsWithTirePickupOptions}
            salesItemId={salesItemId}
            isAddonsAvailable={isAddonsAvailable}
            isWheelPickupRequired={isWheelPickupRequired}
            mutuallyExclusiveSalesItemIds={mutuallyExclusiveSalesItemIds}
            priceFormatted={priceFormatted}
          />
        )}
      </NoddiBasicCard>
    );
  }

  return (
    <NoddiBasicCard className={cn('flex flex-col gap-2')}>
      <span className='text-lg font-medium'>{name}</span>
      <span>{getHardCodedPickupText(selectedSalesItemsCars, 'otherLocation')}</span>

      <span className={cn('font-semibold', isFree && 'text-successDark')}>
        {t`From`} {priceFormatted}
      </span>

      {!useCustomAddress ? (
        <TireHotelLocationsSelect
          selectedLocation={selectedTireHotelLocation}
          onLocationChange={handleTireHotelLocationSelection}
          placeholder={t`Enter your tire hotel`}
          validForAddressId={selectedAddress?.id}
        />
      ) : (
        <AddressSearch
          countryCodes={BOOKING_AVAILABLE_COUNTRY_CODES}
          translations={getCommonTranslations().addressSearch}
          onSelect={(addressSuggestion) => {
            removeSalesItems(params);
            createAddressFromPlaceId({ placeId: addressSuggestion.placeId, brandDomains: [currentBrand] });
          }}
          defaultValue={customPickUpAddress?.metaData?.selectedTireHotelPickUpAddress}
          loading={isPending}
          onClear={() => {
            resetCreateAddressFromPlaceId();
            removeSalesItems(params);
          }}
        />
      )}

      {recoveryFormProps ? (
        <AddressRecoveryForm {...recoveryFormProps} translations={getCommonTranslations().addressSearch.recovery} />
      ) : (
        <AddressError addressError={addressError} />
      )}

      {addressAwaitingDecision && (
        <TireHotelPickupAddressSuggestion
          addressId={addressAwaitingDecision.id}
          onSelectLocation={handleTireHotelLocationSelection}
        />
      )}

      {(customPickUpAddress?.metaData?.selectedTireHotelPickUpAddress?.id || selectedTireHotelLocation) && (
        <MultipleCarSelectionBlock
          carsWithTirePickupOptions={carsWithTirePickupOptions}
          salesItemId={salesItemId}
          isAddonsAvailable={isAddonsAvailable}
          isWheelPickupRequired={isWheelPickupRequired}
          mutuallyExclusiveSalesItemIds={mutuallyExclusiveSalesItemIds}
          priceFormatted={priceFormatted}
        />
      )}
      <NoddiButton
        variant='link'
        size='small'
        className='-ml-2'
        startIcon='Edit'
        onClick={() => {
          // Clear any address creation errors when switching modes
          resetCreateAddressFromPlaceId();
          setUseCustomAddress(!useCustomAddress);
          // Remove any existing selection since we're switching modes
          if (hasAnySelectedSalesItems) {
            removeSalesItems(params);
          }
        }}
      >
        {useCustomAddress ? t`Enter your tire hotel` : t`Enter address instead`}
      </NoddiButton>
      {createdAddress && !createdAddress.data.isInDeliveryArea && (
        <NoddiInfoBanner
          variant='error'
          className='mt-4 text-black'
          texts={[{ value: getCommonTranslations().weDoNotOfferTheseServicesAtThisAddress }]}
        />
      )}
    </NoddiBasicCard>
  );
};
